import React, { useEffect, useMemo, useState } from 'react';
import useSWR from "swr";
import { config } from "../core";
import { useSortBy, useTable } from "react-table";
import { CircularProgress } from "@material-ui/core";
import DashboardCard from "../components/cards/DashboardCard";

const Table = ({ columns, data }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns, data, initialState: {
      sortBy: [
        {
          id: "totalSeconds",
          desc: true
        }
      ]
    },
  }, useSortBy);
  
  return (
    <table
      className="tw-mt-10 tw-min-w-full tw-divide-y tw-divide-gray-200"
      {...getTableProps()}
    >
      <thead>
        {headerGroups.map((headerGroup, i) =>
          <tr key={`${i}`}
            className={"tw-bg-fs-dark-blue tw-bg-opacity-25 tw-rounded-t-lg"} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) =>
              <th key={`${i}-${j}`} width={column.minWidth > 0 ? column.minWidth : null}
                className={`tw-px-6 tw-py-3 tw-text-left tw-leading-4 tw-font-medium tw-uppercase tw-tracking-wider tw-font-bold 
                      ${column.isSorted ? column.isSortedDesc ? `sort-desc` : `sort-asc` : ``}`}
                {...column.getHeaderProps(column.getSortByToggleProps())}
              >
                {column.render("Header")} {
                  column.isSorted
                    ? column.isSortedDesc
                      ?
                      <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-down"/>
                      </div>
                      : <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-up"/>
                      </div>
                    : ''
                }
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr key={`${i}`}{...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                let classnames = "tw-px-6 tw-py-2 ";
                i % 2 ? classnames = classnames + "tw-bg-gray-200" : null;
                return (
                  <td key={`${i}-${j}`} className={classnames} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default function InspectionTimingDashboard() {
  const columns = [
    {
      Header: "Policy #", 
      accessor: "policyNo",
      Cell: props => {
        return (
          <a target="_blank" rel="noreferrer"
            href={`/inspections/${props.row.original.policyNo}/process/pre_r`}>{props.row.original.policyNo}</a>
        );
      }
    },
    { Header: "Address", accessor: "address" },
    { Header: "Type", accessor: "inspectionType" },
    { Header: "Total Time", accessor: "formattedTime" },
    { Header: "Sessions", accessor: "sessionCount" },
    { 
      Header: "Inspectors", 
      accessor: "userNames",
      Cell: props => {
        const names = props.row.original.userNames || [];
        return names.join(", ");
      }
    },
  ];

  const [tableData, setTableData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [selectedInspectionType, setSelectedInspectionType] = useState("All Types");
  const [availableTypes, setAvailableTypes] = useState([]);

  // Build API URL with query parameters
  const apiUrl = useMemo(() => {
    let url = `${config.api.baseUrl}/inspection-timing/dashboard?days=${selectedDays}`;
    if (selectedInspectionType && selectedInspectionType !== "All Types") {
      url += `&inspectionType=${encodeURIComponent(selectedInspectionType)}`;
    }
    return url;
  }, [selectedDays, selectedInspectionType]);

  const { data } = useSWR(apiUrl, { refreshInterval: 0 });

  useEffect(() => {
    if (data) {
      setDataLoading(false);
      if (data.status === 200 && data.result) {
        setTableData(data.result.inspections || []);
        setAvailableTypes(data.result.inspectionTypes || []);
      }
    }
  }, [data]);

  const dayOptions = [
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 60, label: "Last 60 days" },
    { value: 90, label: "Last 90 days" }
  ];

  function changePeriod(days) {
    setDataLoading(true);
    setSelectedDays(days);
  }

  function changeInspectionType(type) {
    setDataLoading(true);
    setSelectedInspectionType(type);
  }

  // Helper function to format duration for summary cards
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const summary = data?.status === 200 ? data?.result?.summary : null;

  return (
    <>
      <h1>Inspection Timing Dashboard</h1>

      <div className="tw-mt-10 tw-flex tw-gap-4 tw-text-lg tw-items-center tw-h-14 tw-flex-wrap">
        <p>Time Period:</p>
        <select 
          className={"tw-rounded-lg"} 
          value={selectedDays} 
          onChange={(e) => changePeriod(parseInt(e.target.value))}
        >
          {dayOptions.map(option =>
            <option key={option.value} value={option.value}>{option.label}</option>
          )}
        </select>

        <p className="tw-ml-4">Inspection Type:</p>
        <select 
          className={"tw-rounded-lg"} 
          value={selectedInspectionType} 
          onChange={(e) => changeInspectionType(e.target.value)}
        >
          <option value="All Types">All Types</option>
          {availableTypes.map(type =>
            <option key={type} value={type}>{type}</option>
          )}
        </select>

        {dataLoading && <CircularProgress color={"primary"}/>}
      </div>

      {summary && 
        <div className={"tw-grid tw-grid-cols-4 tw-gap-16 tw-my-8"}>
          <DashboardCard 
            title={"Total Inspections"} 
            value={summary.totalInspections}
          />
          <DashboardCard 
            title={"Total Time"} 
            value={formatDuration(summary.totalTimeSeconds)}
          />
          <DashboardCard 
            title={"Average Time"} 
            value={formatDuration(summary.averageTimeSeconds)}
          />
          <DashboardCard 
            title={"Top Type"} 
            value={summary.topInspectionType || "N/A"}
          />
        </div>
      }

      <Table columns={columns} data={tableData || []}/>
    </>
  );
}